if (!customElements.get("quick-add-ajax")) {
  customElements.define(
    "quick-add-ajax",
    class QuickAddAjax extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector("button");
        if (!this.button) return;

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onDrawerClick = this.onDrawerClick.bind(this);
        this.onDrawerChange = this.onDrawerChange.bind(this);
        this.handleOptionChange = this.handleOptionChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.closeCart = this.closeCart.bind(this);

        this.button.addEventListener("click", this.onButtonClick);
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.onButtonClick);
      }

      async onButtonClick(event) {
        event.preventDefault();

        if (this.button.disabled || this.button.classList.contains("is-pending")) {
          return;
        }

        this.currentTrigger = event.currentTarget;
        this.releaseDesktopCardButtonFocus();

        const defaultVariantId = this.button.dataset.defaultVariantId;
        const requiresOptions = this.button.dataset.requiresOptions === "true";

        if (!requiresOptions && defaultVariantId) {
          await this.addToCart(defaultVariantId, {
            button: this.button,
          });
          return;
        }

        await this.openDrawer();
      }

      ensureDrawer() {
        const existing = QuickAddAjax.sharedModal;

        if (existing && document.body.contains(existing.modal)) {
          this.modal = existing.modal;
          this.overlay = existing.overlay;
          this.sheet = existing.sheet;
          this.wrapper = existing.wrapper;
          this.closeButton = existing.closeButton;
          this.bindDrawerEvents();
          return;
        }

        const modal = document.createElement("div");
        modal.className = "qa-modal";
        modal.hidden = true;
        modal.innerHTML = `
          <div class="qa-modal__overlay" data-qa-close></div>
          <div class="qa-modal__sheet" role="dialog" aria-modal="true" aria-label="Quick add">
            <div class="qa-modal__topbar">
              <button type="button" class="qa-modal__close" data-qa-close aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="qa-modal__body"></div>
          </div>
        `;

        document.body.appendChild(modal);

        this.modal = modal;
        this.overlay = modal.querySelector(".qa-modal__overlay");
        this.sheet = modal.querySelector(".qa-modal__sheet");
        this.wrapper = modal.querySelector(".qa-modal__body");
        this.closeButton = modal.querySelector(".qa-modal__close");

        QuickAddAjax.sharedModal = {
          modal: this.modal,
          overlay: this.overlay,
          sheet: this.sheet,
          wrapper: this.wrapper,
          closeButton: this.closeButton,
        };

        this.bindDrawerEvents();
      }

      bindDrawerEvents() {
        if (this.modal.dataset.qaBound === "true") {
          this.modal.__qaActive = this;
          return;
        }

        this.modal.__qaActive = this;

        const active = () => this.modal.__qaActive || this;

        this.modal.addEventListener("click", (event) => {
          if (event.target.closest("[data-qa-close]")) {
            event.preventDefault();
            active().closeCart();
            return;
          }
          active().onDrawerClick(event);
        });

        this.modal.addEventListener("change", (event) => {
          if (active().onCustomSizeInteraction(event)) return;
          active().onDrawerChange(event);
        });

        this.modal.addEventListener("focusin", (event) =>
          active().onCustomSizeInteraction(event),
        );

        this.modal.addEventListener("input", (event) =>
          active().onCustomSizeInteraction(event),
        );

        this.modal.addEventListener("keydown", (event) =>
          active().onKeyDown(event),
        );

        this.modal.dataset.qaBound = "true";
      }

      async openDrawer() {
        this.ensureDrawer();
        this.modal.__qaActive = this;
        this.resetState();
        this.showDrawer();
        this.renderSkeleton();

        try {
          this.productData = await this.fetchProductData();
          this.selectedVariant = this.getInitialVariant(this.productData);
          this.renderProduct();
          this.initGallery();
          this.updateProductView();
          this.scheduleLazyContent(
            this.wrapper.querySelector(".quick-add-ajax__panel"),
          );
          this.initInventoryLightning();
          this.customSizeConfig = window.QuickAddCustomSize?.loadConfig?.() || null;
          this.updateCustomSizeVisibility();

          if (this.closeButton) {
            this.closeButton.setAttribute("tabindex", "0");
            this.closeButton.focus({ preventScroll: true });
          }
        } catch (error) {
          this.renderError(error.message || "Unable to load this product.");
        }
      }

      renderSkeleton() {
        if (!this.wrapper) return;

        const shell = document.createElement("div");
        shell.className = "quick-add-ajax__panel quick-add-ajax__panel--skeleton";
        shell.setAttribute("aria-busy", "true");
        shell.innerHTML = `
          <div class="quick-add-ajax__layout">
            <div class="quick-add-ajax__gallery quick-add-ajax__skeleton-gallery" aria-hidden="true">
              <div class="quick-add-ajax__skeleton-gallery-track">
                <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--image"></div>
                <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--image"></div>
                <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--image"></div>
              </div>
            </div>
            <div class="quick-add-ajax__aside">
              <div class="quick-add-ajax__content quick-add-ajax__skeleton-content">
                <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--title"></div>
                <div class="quick-add-ajax__skeleton-meta-row">
                  <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--price"></div>
                  <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--details"></div>
                </div>
                <div class="quick-add-ajax__skeleton-option-group">
                  <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--option-label"></div>
                  <div class="quick-add-ajax__skeleton-swatches">
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--swatch"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--swatch"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--swatch"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--swatch"></span>
                  </div>
                </div>
                <div class="quick-add-ajax__skeleton-option-group">
                  <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--option-label"></div>
                  <div class="quick-add-ajax__skeleton-pills">
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--pill"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--pill"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--pill"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--pill"></span>
                    <span class="quick-add-ajax__skeleton quick-add-ajax__skeleton--pill"></span>
                  </div>
                </div>
                <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--option quick-add-ajax__skeleton--pc"></div>
              </div>
              <div class="quick-add-ajax__skeleton quick-add-ajax__skeleton--cta"></div>
            </div>
          </div>
        `;

        this.wrapper.prepend(shell);
      }

      resetState() {
        this.productData = null;
        this.selectedVariant = null;
        this.pendingButton = null;
        this.galleryImages = [];
        this.currentImageIndex = 0;
        this.inventoryApiData = null;

        const productNode = this.wrapper?.querySelector(".quick-add-ajax__panel");
        productNode?.remove();

        const errorNode = this.wrapper?.querySelector(".quick-add-ajax__error");
        errorNode?.remove();
      }

      isQuickBuyOpen() {
        return Boolean(this.modal?.classList.contains("qa-modal--open"));
      }

      showDrawer() {
        if (!this.modal) return;

        this.modal.hidden = false;
        // Force reflow so the open transition runs from the closed state.
        void this.modal.offsetWidth;
        this.modal.classList.add("qa-modal--open");
        document.documentElement.classList.add("qa-modal-locked");

        if (this.closeButton) {
          this.closeButton.setAttribute("tabindex", "0");
        }
      }

      isDesktopCardQuickAdd() {
        return window.matchMedia("(min-width: 900px)").matches;
      }

      releaseDesktopCardButtonFocus() {
        if (!this.isDesktopCardQuickAdd()) return;
        this.button?.blur();
      }

      restoreDesktopCardButtonFocus() {
        if (!this.isDesktopCardQuickAdd()) {
          this.currentTrigger?.focus();
          return;
        }

        this.currentTrigger?.blur();
      }

      closeCart() {
        if (!this.modal) return;

        window.QuickAddSizeGuide?.close?.();

        this.modal.classList.remove("qa-modal--open");
        document.documentElement.classList.remove("qa-modal-locked");

        const modal = this.modal;
        const onEnd = () => {
          if (!modal.classList.contains("qa-modal--open")) {
            modal.hidden = true;
          }
        };
        const sheet = this.sheet;
        if (sheet) {
          sheet.addEventListener("transitionend", onEnd, { once: true });
          setTimeout(onEnd, 400);
        } else {
          modal.hidden = true;
        }

        this.restoreDesktopCardButtonFocus();
      }

      getProductUrl() {
        return (
          this.button.dataset.productUrl ||
          this.getAttribute("data-product-url") ||
          `/products/${this.getAttribute("data-product-handle")}`
        );
      }

      getProductJsonUrl() {
        const productUrl = new URL(this.getProductUrl(), window.location.origin);
        let pathname = productUrl.pathname.replace(/\/$/, "");

        if (!pathname.endsWith(".js")) {
          pathname = `${pathname}.js`;
        }

        return `${pathname}${productUrl.search}`;
      }

      async fetchProductData() {
        const response = await fetch(this.getProductJsonUrl(), {
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Unable to load this product.");
        }

        return response.json();
      }

      getPreferredVariantId() {
        const fromButton = Number(this.button?.dataset.defaultVariantId);
        if (fromButton) return fromButton;

        try {
          const productUrl = new URL(this.getProductUrl(), window.location.origin);
          const variantParam = productUrl.searchParams.get("variant");
          if (variantParam) {
            const variantId = Number(variantParam);
            if (variantId) return variantId;
          }
        } catch (error) {
          return null;
        }

        return null;
      }

      getInitialVariant(product) {
        const preferredVariantId = this.getPreferredVariantId();
        if (preferredVariantId) {
          const preferredVariant = product.variants.find(
            (variant) => variant.id === preferredVariantId,
          );
          if (preferredVariant) return preferredVariant;
        }

        return (
          product.variants.find((variant) => variant.available) || product.variants[0]
        );
      }

      parseJsonAttribute(attributeName) {
        const rawValue = this.getAttribute(attributeName);
        if (!rawValue) return [];

        try {
          return JSON.parse(rawValue);
        } catch (error) {
          return [];
        }
      }

      getColorOptionNames() {
        if (!this.colorOptionNames) {
          this.colorOptionNames = this.parseJsonAttribute("data-color-option-names")
            .map((value) => String(value || "").trim().toLowerCase())
            .filter(Boolean);
        }

        return this.colorOptionNames;
      }

      getSwatchesConfig() {
        if (!this.swatchesConfig) {
          this.swatchesConfig = this.parseJsonAttribute("data-swatches-config");
        }

        return this.swatchesConfig;
      }

      usesRoundSwatches() {
        return this.getAttribute("data-round-swatches") === "true";
      }

      getSelectedOptions() {
        if (!this.wrapper || !this.productData) return [];

        return this.productData.options.map((_, index) => {
          const checkedRadio = this.wrapper.querySelector(
            `[data-quick-add-option][data-option-index="${index}"][type="radio"]:checked`,
          );
          if (checkedRadio) return checkedRadio.value;

          const select = this.wrapper.querySelector(
            `select[data-quick-add-option][data-option-index="${index}"]`,
          );

          return select?.value || "";
        });
      }

      findVariantByOptions(optionValues) {
        if (!this.productData) return null;

        return this.productData.variants.find((variant) =>
          optionValues.every(
            (optionValue, index) =>
              variant[`option${index + 1}`] === optionValue,
          ),
        );
      }

      findClosestVariant(changedIndex, optionValues) {
        if (!this.productData) return null;

        const changedValue = optionValues[changedIndex];
        if (!changedValue) return null;

        const candidates = this.productData.variants.filter(
          (variant) => variant[`option${changedIndex + 1}`] === changedValue,
        );

        return (
          candidates.find((variant) => variant.available) ||
          candidates[0] ||
          null
        );
      }

      syncOptionInputs() {
        if (!this.wrapper || !this.selectedVariant || !this.productData) return;

        this.productData.options.forEach((_, index) => {
          const selectedValue = this.selectedVariant[`option${index + 1}`];
          const inputs = this.wrapper.querySelectorAll(
            `[data-quick-add-option][data-option-index="${index}"]`,
          );

          inputs.forEach((input) => {
            if (input.type === "radio") {
              const isSelected = input.value === selectedValue;
              input.checked = isSelected;

              const pill = input.nextElementSibling;
              if (pill?.classList.contains("quick-add-ajax__pill")) {
                pill.classList.toggle("is-selected", isSelected);
              }

              return;
            }

            input.value = selectedValue;
          });
        });
      }

      isOptionValueAvailable(index, value, selections = this.getSelectedOptions()) {
        if (!this.productData) return false;

        const candidateSelections = [...selections];
        candidateSelections[index] = value;

        return this.productData.variants.some(
          (variant) =>
            variant.available &&
            candidateSelections.every(
              (selectedValue, optionIndex) =>
                !selectedValue ||
                variant[`option${optionIndex + 1}`] === selectedValue,
            ),
        );
      }

      updateOptionAvailability() {
        if (!this.wrapper || !this.productData) return;

        const selections = this.getSelectedOptions();

        this.productData.options.forEach((_, index) => {
          const radioInputs = this.wrapper.querySelectorAll(
            `[data-quick-add-option][data-option-index="${index}"][type="radio"]`,
          );

          radioInputs.forEach((input) => {
            const isAvailable = this.isOptionValueAvailable(
              index,
              input.value,
              selections,
            );
            input.disabled = !isAvailable;
            input.classList.toggle("disabled", !isAvailable);
          });

          const select = this.wrapper.querySelector(
            `select[data-quick-add-option][data-option-index="${index}"]`,
          );

          if (!select) return;

          Array.from(select.options).forEach((optionElement) => {
            optionElement.disabled = !this.isOptionValueAvailable(
              index,
              optionElement.value,
              selections,
            );
          });
        });
      }

      isColorOption(option, index) {
        const optionLabel = this.getOptionLabel(option, index)
          .trim()
          .toLowerCase();

        return this.getColorOptionNames().includes(optionLabel);
      }

      renderProduct() {
        if (!this.wrapper || !this.productData || !this.selectedVariant) return;

        const product = this.productData;
        const variant = this.selectedVariant;
        const imageSrc = this.getVariantImage(variant, product);
        const productImages = this.getProductImages(product);
        const initialImageIndex = Math.max(0, productImages.indexOf(imageSrc));
        const productUrl = this.getProductUrl();
        const detailsLabel =
          this.button.dataset.viewDetailsLabel || "Details";
        const optionMarkup = product.options
          .map((option, index) =>
            this.renderOptionControl(option, index, variant),
          )
          .join("");
        const customSizeMarkup = window.QuickAddCustomSize?.renderPanel
          ? window.QuickAddCustomSize.renderPanel(
              this.customSizeConfig ||
                window.QuickAddCustomSize.loadConfig?.() ||
                {},
            )
          : "";

        const mobileSlidesMarkup = productImages
          .map(
            (url, index) => `
              <div
                class="quick-add-ajax__gallery-slide${
                  index === initialImageIndex ? " is-active" : ""
                }"
                data-qa-gallery-slide="${index}"
              >
                <img
                  class="quick-add-ajax__gallery-slide__img"
                  data-src="${this.escapeHtml(url)}"
                  alt="${this.escapeHtml(product.title)}"
                >
              </div>
            `,
          )
          .join("");

        const content = document.createElement("div");
        content.className = "wt-product quick-add-ajax__panel";
        content.innerHTML = `
          <div class="quick-add-ajax__layout">
            <div class="quick-add-ajax__gallery" data-qa-gallery>
              <div class="quick-add-ajax__gallery-desktop">
                <button
                  type="button"
                  class="quick-add-ajax__gallery-nav quick-add-ajax__gallery-nav--prev"
                  data-qa-gallery-prev
                  aria-label="Previous image"
                  hidden
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div class="quick-add-ajax__image-wrap">
                  <img
                    class="quick-add-ajax__image"
                    data-src="${this.escapeHtml(imageSrc)}"
                    alt="${this.escapeHtml(product.title)}"
                  >
                </div>
                <button
                  type="button"
                  class="quick-add-ajax__gallery-nav quick-add-ajax__gallery-nav--next"
                  data-qa-gallery-next
                  aria-label="Next image"
                  hidden
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              <div
                class="quick-add-ajax__gallery-mobile"
                data-qa-gallery-track
              >
                ${mobileSlidesMarkup}
              </div>
            </div>
            <div class="quick-add-ajax__aside">
            <div class="quick-add-ajax__content">
              <div class="quick-add-ajax__meta">
                <div class="wt-product__name">
                  <a class="quick-add-ajax__title-link" href="${this.escapeHtml(
                    productUrl,
                  )}">${this.escapeHtml(product.title)}</a>
                </div>
                <div class="quick-add-ajax__meta-row">
                  <div class="wt-product__price quick-add-ajax__price"></div>
                  <a class="quick-add-ajax__details-link" href="${this.escapeHtml(
                    productUrl,
                  )}">${this.escapeHtml(detailsLabel)}</a>
                </div>
              </div>
              <div
                class="quick-add-ajax__stock-message"
                data-qa-stock-message
                hidden
              ></div>
              <div class="wt-product__option quick-add-ajax__options">
                ${optionMarkup}
              </div>
              ${customSizeMarkup}
              <div class="product-form__error-message-wrapper quick-add-ajax__error-message" hidden>
                <span class="product-form__error-message"></span>
              </div>
            </div>
            <div class="wt-product__add-to-cart main-product__buy-buttons--container quick-add-ajax__footer">
              <div class="wt-product__add-to-cart_form form">
                <div class="wt-product__add-to-cart_form--wrapper">
                  <button
                    type="button"
                    class="button--full-width hero__button--primary quick-add-ajax__submit"
                    data-quick-add-submit
                  >
                    <span class="quick-add-ajax__submit-label"></span>
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>
        `;

        const existingProduct = this.wrapper.querySelector(".quick-add-ajax__panel");
        existingProduct?.remove();
        this.wrapper.prepend(content);
        this.currentImageIndex = initialImageIndex;
        content.removeAttribute("aria-busy");
        this.updateCustomSizeVisibility();
      }

      scheduleLazyContent(panel) {
        if (!panel) return;

        requestAnimationFrame(() => {
          this.hydrateLazyImages(panel);
        });
      }

      loadGalleryImage(img, src, { animateSwitch = false } = {}) {
        if (!img || !src) return;

        const wrap = img.closest(".quick-add-ajax__image-wrap");
        wrap?.classList.add("is-loading");

        if (animateSwitch) {
          img.classList.remove("is-loaded");
          img.classList.add("is-switching");
        } else {
          img.classList.remove("is-loaded", "is-switching");
        }

        const finishLoading = () => {
          wrap?.classList.remove("is-loading");
          img.classList.remove("is-switching");
          img.classList.add("is-loaded");

          const gallery = img.closest("[data-qa-gallery]");
          gallery?.classList.remove(
            "quick-add-ajax__gallery--slide-left",
            "quick-add-ajax__gallery--slide-right",
          );
        };

        const onLoad = () => {
          finishLoading();
          img.removeEventListener("load", onLoad);
          img.removeEventListener("error", onError);
        };

        const onError = () => {
          wrap?.classList.remove("is-loading");
          img.classList.remove("is-switching");
          img.removeEventListener("load", onLoad);
          img.removeEventListener("error", onError);
        };

        img.addEventListener("load", onLoad);
        img.addEventListener("error", onError);
        img.loading = "lazy";
        img.decoding = "async";
        img.src = src;
        img.removeAttribute("data-src");

        if (img.complete && img.naturalWidth) {
          onLoad();
        }
      }

      hydrateLazyImages(root) {
        if (!root) return;

        root.querySelectorAll("img[data-src]").forEach((img) => {
          const src = img.getAttribute("data-src");
          if (!src) return;

          this.loadGalleryImage(img, src);
        });
      }

      bindMobileGalleryScroll() {
        const track = this.wrapper?.querySelector("[data-qa-gallery-track]");
        if (!track || track.dataset.qaScrollBound === "true") return;

        track.dataset.qaScrollBound = "true";

        let scrollTimer;
        track.addEventListener(
          "scroll",
          () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(
              () => this.syncGalleryIndexFromScroll(track),
              120,
            );
          },
          { passive: true },
        );
      }

      syncGalleryIndexFromScroll(track = null) {
        const galleryTrack =
          track || this.wrapper?.querySelector("[data-qa-gallery-track]");
        if (!galleryTrack) return;

        const slides = [
          ...galleryTrack.querySelectorAll("[data-qa-gallery-slide]"),
        ];
        if (!slides.length) return;

        const trackCenter =
          galleryTrack.scrollLeft + galleryTrack.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(trackCenter - slideCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex === this.currentImageIndex) return;

        this.currentImageIndex = closestIndex;
        this.updateMobileGallerySlides();
        this.updateDesktopGalleryImage(false);
      }

      updateMobileGallerySlides(shouldScroll = false) {
        const track = this.wrapper?.querySelector("[data-qa-gallery-track]");
        if (!track) return;

        const slides = track.querySelectorAll("[data-qa-gallery-slide]");
        slides.forEach((slide, index) => {
          slide.classList.toggle("is-active", index === this.currentImageIndex);
        });

        if (!shouldScroll) return;

        const scrollToActive = () => {
          const activeSlide = slides[this.currentImageIndex];
          if (!activeSlide) return;

          track.scrollTo({
            left:
              activeSlide.offsetLeft -
              (track.clientWidth - activeSlide.offsetWidth) / 2,
            behavior: "smooth",
          });
        };

        requestAnimationFrame(scrollToActive);
      }

      updateDesktopGalleryImage(shouldHydrate = true) {
        const gallery = this.wrapper?.querySelector("[data-qa-gallery]");
        if (!gallery || !this.galleryImages?.length) return;

        const imageNode = gallery.querySelector(
          ".quick-add-ajax__gallery-desktop .quick-add-ajax__image",
        );
        const prevButton = gallery.querySelector("[data-qa-gallery-prev]");
        const nextButton = gallery.querySelector("[data-qa-gallery-next]");
        const hasMultiple = this.galleryImages.length > 1;
        const imageUrl = this.galleryImages[this.currentImageIndex];

        if (prevButton) {
          prevButton.hidden = !hasMultiple;
          prevButton.disabled = this.currentImageIndex === 0;
        }

        if (nextButton) {
          nextButton.hidden = !hasMultiple;
          nextButton.disabled =
            this.currentImageIndex === this.galleryImages.length - 1;
        }

        if (!imageNode || !imageUrl) return;

        if (shouldHydrate) {
          this.loadGalleryImage(imageNode, imageUrl, { animateSwitch: true });
        } else {
          imageNode.setAttribute("data-src", imageUrl);
        }
      }

      getProductImages(product) {
        const images = [];

        if (Array.isArray(product?.images)) {
          product.images.forEach((entry) => {
            const url = this.getImageUrlFromEntry(entry);
            if (url) images.push(url);
          });
        }

        if (!images.length) {
          const fallback = this.getVariantImage(
            product?.variants?.[0],
            product,
          );
          if (fallback) images.push(fallback);
        }

        return [...new Set(images)];
      }

      initGallery() {
        if (!this.productData) return;

        this.galleryImages = this.getProductImages(this.productData);
        const variantImage = this.getVariantImage(
          this.selectedVariant,
          this.productData,
        );
        const matchedIndex = this.findGalleryImageIndex(
          variantImage,
          this.selectedVariant,
        );

        this.currentImageIndex = matchedIndex >= 0 ? matchedIndex : 0;
        this.bindMobileGalleryScroll();
        this.updateGalleryView(false);
        requestAnimationFrame(() => {
          const track = this.wrapper?.querySelector("[data-qa-gallery-track]");
          const slides = track?.querySelectorAll("[data-qa-gallery-slide]");
          const activeSlide = slides?.[this.currentImageIndex];
          if (!track || !activeSlide) return;

          track.scrollLeft =
            activeSlide.offsetLeft -
            (track.clientWidth - activeSlide.offsetWidth) / 2;
        });
      }

      updateGalleryView(shouldHydrate = true) {
        if (!this.galleryImages?.length) return;

        this.updateDesktopGalleryImage(shouldHydrate);
        this.updateMobileGallerySlides(shouldHydrate);

        if (!shouldHydrate) return;

        const track = this.wrapper?.querySelector("[data-qa-gallery-track]");
        const slide = track?.querySelector(
          `[data-qa-gallery-slide="${this.currentImageIndex}"]`,
        );
        const img = slide?.querySelector(".quick-add-ajax__gallery-slide__img");
        const imageUrl = this.galleryImages[this.currentImageIndex];

        if (img && imageUrl) {
          this.loadGalleryImage(img, imageUrl, { animateSwitch: true });
        }
      }

      navigateGallery(delta) {
        if (!this.galleryImages?.length) return;

        const nextIndex = this.currentImageIndex + delta;
        if (nextIndex < 0 || nextIndex >= this.galleryImages.length) return;

        const gallery = this.wrapper?.querySelector("[data-qa-gallery]");
        gallery?.classList.toggle(
          "quick-add-ajax__gallery--slide-left",
          delta > 0,
        );
        gallery?.classList.toggle(
          "quick-add-ajax__gallery--slide-right",
          delta < 0,
        );

        this.currentImageIndex = nextIndex;
        this.updateGalleryView(true);
      }

      renderOptionControl(option, index, variant) {
        if (this.isColorOption(option, index)) {
          return this.renderColorSwatches(option, index, variant);
        }

        const values = this.getOptionValues(option, index);
        if (values.length <= 15) {
          return this.renderOptionPills(option, index, variant);
        }

        return this.renderOptionSelect(option, index, variant);
      }

      renderOptionHeading(optionLabel, selectedValue = "") {
        if (window.QuickAddSizeGuide?.renderOptionHeader) {
          return window.QuickAddSizeGuide.renderOptionHeader(
            optionLabel,
            selectedValue,
          );
        }

        const hasSelectedValue = Boolean(selectedValue);

        return `
          <div class="wt-product__option__title quick-add-ajax__option-title">
            <span class="quick-add-ajax__option-name" data-option-name="${this.escapeHtml(
              optionLabel,
            )}">${this.escapeHtml(optionLabel)}${
              hasSelectedValue ? ":" : ""
            }</span>
            ${
              hasSelectedValue
                ? `<span class="quick-add-ajax__option-value">${this.escapeHtml(
                    selectedValue,
                  )}</span>`
                : ""
            }
          </div>
        `;
      }

      renderOptionPills(option, index, variant) {
        const values = this.getOptionValues(option, index);
        const selectedValue = variant[`option${index + 1}`] || values[0] || "";
        const optionLabel = this.getOptionLabel(option, index);
        const inputName = `quick-add-option-${this.productData.handle}-${index}`;
        const currentSelections = this.productData.options.map(
          (_, optionIndex) => variant[`option${optionIndex + 1}`] || "",
        );

        const items = values
          .map((value, valueIndex) => {
            const inputId = `${inputName}-${valueIndex}`;
            const isChecked = value === selectedValue;
            const isAvailable = this.isOptionValueAvailable(
              index,
              value,
              currentSelections,
            );

            return `
              <li class="f-button__list__item">
                <input
                  type="radio"
                  id="${this.escapeHtml(inputId)}"
                  name="${this.escapeHtml(inputName)}"
                  value="${this.escapeHtml(value)}"
                  data-quick-add-option
                  data-option-index="${index}"
                  aria-label="${this.escapeHtml(value)}"
                  ${isChecked ? "checked" : ""}
                  ${isAvailable ? "" : 'class="disabled"'}
                  ${isAvailable ? "" : "disabled"}
                >
                <label
                  class="f-button__list__link quick-add-ajax__pill"
                  for="${this.escapeHtml(inputId)}"
                  data-inventory-size-key="${this.escapeHtml(
                    this.normalizeInventoryKey(value),
                  )}"
                >
                  ${this.escapeHtml(value)}
                </label>
              </li>
            `;
          })
          .join("");

        return `
          <div class="wt-product__option__wrapper quick-add-ajax__option-group" data-option-index="${index}">
            ${this.renderOptionHeading(optionLabel, selectedValue)}
            <div class="wt-product__option__body wt-product__option__body--tiles">
              <ul class="f-button__list quick-add-ajax__pill-list">
                ${items}
              </ul>
            </div>
          </div>
        `;
      }

      renderColorSwatches(option, index, variant) {
        const values = this.getOptionValues(option, index);
        const selectedValue = variant[`option${index + 1}`] || values[0] || "";
        const optionLabel = this.getOptionLabel(option, index);
        const roundedClass = this.usesRoundSwatches() ? " rounded" : "";
        const inputName = `quick-add-option-${this.productData.handle}-${index}`;
        const currentSelections = this.productData.options.map(
          (_, optionIndex) => variant[`option${optionIndex + 1}`] || "",
        );

        const items = values
          .map((value, valueIndex) => {
            const inputId = `${inputName}-${valueIndex}`;
            const isChecked = value === selectedValue;
            const isAvailable = this.isOptionValueAvailable(
              index,
              value,
              currentSelections,
            );
            const swatch = this.getSwatchPresentation(value, index);

            return `
              <li class="f-swatch__list__item">
                <input
                  type="radio"
                  id="${this.escapeHtml(inputId)}"
                  name="${this.escapeHtml(inputName)}"
                  value="${this.escapeHtml(value)}"
                  data-quick-add-option
                  data-option-index="${index}"
                  aria-label="${this.escapeHtml(value)}"
                  ${isChecked ? "checked" : ""}
                  ${isAvailable ? "" : 'class="disabled"'}
                  ${isAvailable ? "" : "disabled"}
                >
                <label
                  class="f-swatch__list__link${roundedClass}"
                  for="${this.escapeHtml(inputId)}"
                  title="${this.escapeHtml(value)}"
                  data-inventory-color-key="${this.escapeHtml(
                    this.normalizeInventoryKey(value),
                  )}"
                >
                  <div
                    class="f-swatch__list__link__content${roundedClass}"
                    style="${this.escapeHtml(swatch.style)}"
                  >${swatch.text}</div>
                  <span class="visually-hidden">${this.escapeHtml(value)}</span>
                </label>
              </li>
            `;
          })
          .join("");

        return `
          <div class="wt-product__option__wrapper quick-add-ajax__option-group" data-option-index="${index}">
            ${this.renderOptionHeading(optionLabel, selectedValue)}
            <div class="wt-product__option__body wt-product__option__body--swatches">
              <ul class="f-swatch__list">
                ${items}
              </ul>
            </div>
          </div>
        `;
      }

      renderOptionSelect(option, index, variant) {
        const values = this.getOptionValues(option, index);
        const selectedValue = variant[`option${index + 1}`] || values[0] || "";
        const optionLabel = this.getOptionLabel(option, index);

        const options = values
          .map(
            (value) => `
              <option value="${this.escapeHtml(value)}" ${
                value === selectedValue ? "selected" : ""
              }>
                ${this.escapeHtml(value)}
              </option>
            `,
          )
          .join("");

        return `
          <div class="wt-product__option__wrapper quick-add-ajax__option-group" data-option-index="${index}">
            ${this.renderOptionHeading(optionLabel, selectedValue)}
            <div class="wt-product__option__dropdown">
              <select data-quick-add-option data-option-index="${index}">
                ${options}
              </select>
            </div>
          </div>
        `;
      }

      getOptionLabel(option, index) {
        if (typeof option === "string") return option;
        if (option && typeof option === "object" && option.name) {
          return option.name;
        }

        return `Option ${index + 1}`;
      }

      getSwatchImages() {
        if (!this.swatchImages) {
          this.swatchImages = this.parseJsonAttribute("data-swatch-images");
        }

        return this.swatchImages;
      }

      normalizeOptionValue(value) {
        if (typeof value === "string") return value;
        if (value && typeof value === "object") {
          return value.name || value.value || "";
        }

        return "";
      }

      getOptionValues(option, index) {
        if (
          option &&
          typeof option === "object" &&
          Array.isArray(option.values) &&
          option.values.length
        ) {
          return [
            ...new Set(
              option.values
                .map((value) => this.normalizeOptionValue(value))
                .filter(Boolean),
            ),
          ];
        }

        const key = `option${index + 1}`;
        const values = this.productData.variants.map((variant) => variant[key]);
        return [...new Set(values.filter(Boolean))];
      }

      getVariantSwatchImage(optionIndex, value) {
        if (!this.productData?.variants) return "";

        const optionKey = `option${optionIndex + 1}`;
        const variant = this.productData.variants.find(
          (entry) =>
            entry[optionKey] === value &&
            (entry.featured_image || entry.image_id),
        );

        if (!variant) return "";

        return this.getVariantImage(variant, this.productData) || "";
      }

      getNativeOptionSwatch(optionIndex, value) {
        const option = this.productData?.options?.[optionIndex];
        if (!option || typeof option !== "object" || !Array.isArray(option.values)) {
          return "";
        }

        const entry = option.values.find((candidate) => {
          const candidateValue = this.normalizeOptionValue(candidate);
          return candidateValue === value;
        });

        if (!entry || typeof entry === "string" || !entry.swatch) return "";

        if (entry.swatch.image) {
          const imageUrl =
            typeof entry.swatch.image === "string"
              ? entry.swatch.image
              : entry.swatch.image.src || entry.swatch.image.url || "";

          if (imageUrl) {
            return this.buildSwatchImageStyle(imageUrl);
          }
        }

        if (entry.swatch.color) {
          if (typeof entry.swatch.color === "string") {
            return `background-color: ${entry.swatch.color};`;
          }

          if (entry.swatch.color.rgb) {
            return `background-color: rgb(${entry.swatch.color.rgb});`;
          }
        }

        return "";
      }

      buildSwatchImageStyle(imageUrl) {
        const safeUrl = String(imageUrl).replace(/"/g, '\\"');
        return `background-image: url("${safeUrl}"); background-size: cover; background-position: center;`;
      }

      getConfigSwatchStyle(normalizedValue) {
        const matchedConfig = this.getSwatchesConfig().find((item) => {
          const [name] = String(item || "").split(":");
          return name && name.trim().toLowerCase() === normalizedValue;
        });

        if (!matchedConfig) return "";

        const [, rawConfigValue = ""] = matchedConfig.split(":");
        const configValue = rawConfigValue.trim();

        if (configValue.startsWith("#")) {
          if (configValue.length === 7) {
            return `background-color: ${configValue};`;
          }

          if (configValue.length === 14) {
            const firstColor = configValue.slice(0, 7);
            const secondColor = configValue.slice(7, 14);
            return `background: linear-gradient(45deg, ${firstColor} 50%, ${secondColor} 50%);`;
          }

          if (configValue.length === 21) {
            const firstColor = configValue.slice(0, 7);
            const secondColor = configValue.slice(7, 14);
            const thirdColor = configValue.slice(14, 21);
            return `background: linear-gradient(45deg, ${firstColor} 0% 35%, ${secondColor} 35% 65%, ${thirdColor} 65% 100%);`;
          }
        }

        if (configValue && CSS.supports("color", configValue)) {
          return `background-color: ${configValue};`;
        }

        return "";
      }

      getSwatchPresentation(value, optionIndex = 0) {
        const swatchStyle = this.getSwatchStyle(value, optionIndex);

        if (swatchStyle) {
          return {
            style: swatchStyle,
            text: "",
          };
        }

        return {
          style:
            "background: linear-gradient(135deg, #f7f7f7 0%, #ececec 100%); display: flex; align-items: center; justify-content: center; color: var(--color-body-text); font-size: 10px; font-weight: 600; text-transform: uppercase;",
          text: `<span>${this.escapeHtml(String(value || "").slice(0, 2))}</span>`,
        };
      }

      getSwatchStyle(value, optionIndex = 0) {
        const swatchValue = String(value || "").trim();
        const normalizedValue = swatchValue.toLowerCase();

        const nativeSwatch = this.getNativeOptionSwatch(optionIndex, swatchValue);
        if (nativeSwatch) return nativeSwatch;

        const imageEntry = this.getSwatchImages().find(
          (entry) => entry.name?.trim().toLowerCase() === normalizedValue,
        );
        if (imageEntry?.url) {
          return this.buildSwatchImageStyle(imageEntry.url);
        }

        const variantImage = this.getVariantSwatchImage(optionIndex, swatchValue);
        if (variantImage) {
          return this.buildSwatchImageStyle(variantImage);
        }

        const configStyle = this.getConfigSwatchStyle(normalizedValue);
        if (configStyle) return configStyle;

        if (normalizedValue.includes("leopard")) {
          return "background: radial-gradient(circle at 30% 30%, #2b1a0f 0 12%, transparent 13%), radial-gradient(circle at 70% 55%, #4a2d18 0 10%, transparent 11%), linear-gradient(135deg, #e6c48e 0%, #c58d4f 100%);";
        }

        if (CSS.supports("color", normalizedValue)) {
          return `background-color: ${normalizedValue};`;
        }

        const collapsedValue = normalizedValue.replace(/\s+/g, "");
        if (CSS.supports("color", collapsedValue)) {
          return `background-color: ${collapsedValue};`;
        }

        return "";
      }

      updateProductView() {
        if (!this.wrapper || !this.productData) return;

        const variant = this.selectedVariant;
        const priceNode = this.wrapper.querySelector(".quick-add-ajax__price");
        const submitButton = this.wrapper.querySelector("[data-quick-add-submit]");
        const submitLabel = this.wrapper.querySelector(
          ".quick-add-ajax__submit-label",
        );
        const productUrl = this.getProductUrl();

        if (variant) {
          const variantImage = this.getVariantImage(variant, this.productData);
          const matchedIndex = this.findGalleryImageIndex(
            variantImage,
            variant,
          );

          if (matchedIndex >= 0) {
            this.currentImageIndex = matchedIndex;
            this.updateGalleryView(true);
          } else if (variantImage) {
            const imageNode = this.wrapper.querySelector(
              ".quick-add-ajax__gallery-desktop .quick-add-ajax__image",
            );
            if (imageNode) {
              this.loadGalleryImage(imageNode, variantImage, {
                animateSwitch: true,
              });
              imageNode.alt = this.productData.title;
            }
            this.updateMobileGalleryImage(variantImage, { animateSwitch: true });
          }
        }

        if (priceNode && variant) {
          priceNode.innerHTML = this.renderPriceMarkup(variant);
        }

        const titleLink = this.wrapper.querySelector(".quick-add-ajax__title-link");
        const detailsLink = this.wrapper.querySelector(".quick-add-ajax__details-link");
        if (titleLink) {
          titleLink.href = productUrl;
        }
        if (detailsLink) {
          detailsLink.href = productUrl;
        }

        this.syncOptionInputs();
        this.updateOptionHeadings();
        this.updateOptionAvailability();
        this.applyLightningIcons();
        this.updateStockMessage();
        this.updateCustomSizeVisibility();

        if (!submitButton || !submitLabel) return;

        if (!variant) {
          submitButton.disabled = true;
          submitLabel.textContent =
            this.button.dataset.unavailableLabel ||
            window.variantStrings?.unavailable ||
            "Unavailable";
          return;
        }

        submitButton.disabled = !variant.available;
        submitButton.dataset.variantId = String(variant.id);
        submitLabel.textContent = variant.available
          ? this.button.dataset.addToCartLabel ||
            window.variantStrings?.addToCart ||
            "Add to cart"
          : this.button.dataset.soldOutLabel ||
            window.variantStrings?.soldOut ||
            "Sold out";
      }

      renderPriceMarkup(variant) {
        const price = this.formatMoney(variant.price);
        const compareAtPrice = variant.compare_at_price;

        if (compareAtPrice && compareAtPrice > variant.price) {
          return `
            <span class="price-item price-item--sale">${price}</span>
            <s class="price-item price-item--regular">${this.formatMoney(
              compareAtPrice,
            )}</s>
          `;
        }

        return `<span class="price-item price-item--regular">${price}</span>`;
      }

      normalizeImageUrl(url) {
        if (!url) return "";

        const value = typeof url === "string" ? url : url?.src || url?.url || "";
        if (!value) return "";

        try {
          return new URL(value, window.location.origin).pathname;
        } catch {
          return value.split("?")[0];
        }
      }

      getImageUrlFromEntry(entry) {
        if (!entry) return "";
        if (typeof entry === "string") return entry;
        return entry.src || entry.url || "";
      }

      findGalleryImageIndex(imageUrl, variant = null) {
        if (!this.galleryImages?.length) return -1;

        if (imageUrl) {
          const normalizedTarget = this.normalizeImageUrl(imageUrl);
          const targetFile = normalizedTarget.split("/").pop();

          let index = this.galleryImages.findIndex(
            (entry) => this.normalizeImageUrl(entry) === normalizedTarget,
          );
          if (index >= 0) return index;

          if (targetFile) {
            index = this.galleryImages.findIndex((entry) => {
              const normalizedEntry = this.normalizeImageUrl(entry);
              return (
                normalizedEntry.split("/").pop() === targetFile ||
                normalizedEntry.includes(targetFile)
              );
            });
            if (index >= 0) return index;
          }
        }

        if (variant?.image_id) {
          const imageId = String(variant.image_id);
          const index = this.galleryImages.findIndex((entry) => {
            const url = this.getImageUrlFromEntry(entry);
            return (
              url.includes(imageId) ||
              String(entry?.id || "") === imageId
            );
          });
          if (index >= 0) return index;
        }

        return -1;
      }

      updateMobileGalleryImage(imageUrl, { animateSwitch = false } = {}) {
        const track = this.wrapper?.querySelector("[data-qa-gallery-track]");
        if (!track || !imageUrl) return;

        const slide =
          track.querySelector(
            `[data-qa-gallery-slide="${this.currentImageIndex}"]`,
          ) ||
          track.querySelector(".quick-add-ajax__gallery-slide.is-active") ||
          track.querySelector("[data-qa-gallery-slide]");
        const img = slide?.querySelector(".quick-add-ajax__gallery-slide__img");

        if (!img) return;

        this.loadGalleryImage(img, imageUrl, { animateSwitch });
      }

      getVariantImage(variant, product) {
        if (variant?.featured_image) {
          const featured = this.getImageUrlFromEntry(variant.featured_image);
          if (featured) return featured;
        }

        if (variant?.image_id && Array.isArray(product?.images)) {
          const matched = product.images.find((entry) => {
            const url = this.getImageUrlFromEntry(entry);
            const id = String(entry?.id || variant.image_id);
            return (
              String(entry?.id || "") === String(variant.image_id) ||
              url.includes(String(variant.image_id)) ||
              id === String(variant.image_id)
            );
          });
          const matchedUrl = this.getImageUrlFromEntry(matched);
          if (matchedUrl) return matchedUrl;
        }

        const fallback = product?.featured_image || product?.images?.[0] || "";
        return this.getImageUrlFromEntry(fallback);
      }

      static LIGHTNING_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" class="icon-x" width="15" height="20" viewBox="0 0 15 20" aria-hidden="true"><path d="M14.5833 8H8.61742L9.94318 0L0 12H5.96591L4.64015 20L14.5833 8" fill="#EFBF04"></path></svg>';

      static LIGHTNING_OVERLAY_SVG =
        '<svg xmlns="http://www.w3.org/2000/svg" class="icon-x" width="13" height="17" viewBox="0 0 15 20" aria-hidden="true"><path d="M14.5833 8H8.61742L9.94318 0L0 12H5.96591L4.64015 20L14.5833 8" fill="none" stroke="#FFFFFF" stroke-width="1"></path></svg>';

      normalizeInventoryKey(value) {
        return String(value || "")
          .trim()
          .replace(/"/g, "")
          .replace(/ /g, "_");
      }

      getColorOptionIndex() {
        if (!this.productData?.options?.length) return -1;

        for (let index = 0; index < this.productData.options.length; index++) {
          if (this.isColorOption(this.productData.options[index], index)) {
            return index;
          }
        }

        return -1;
      }

      getSelectedColorKey() {
        const colorIndex = this.getColorOptionIndex();
        if (colorIndex < 0 || !this.selectedVariant) return "";

        return this.normalizeInventoryKey(
          this.selectedVariant[`option${colorIndex + 1}`],
        );
      }

      getSizeOptionIndex() {
        if (!this.productData?.options?.length) return -1;

        for (let index = 0; index < this.productData.options.length; index++) {
          const optionLabel = this.getOptionLabel(
            this.productData.options[index],
            index,
          )
            .trim()
            .toLowerCase();

          if (optionLabel === "size") return index;
        }

        return -1;
      }

      getSelectedSizeKey() {
        const sizeIndex = this.getSizeOptionIndex();
        if (sizeIndex < 0 || !this.selectedVariant) return "";

        return this.normalizeInventoryKey(
          this.selectedVariant[`option${sizeIndex + 1}`],
        );
      }

      updateStockMessage() {
        const messageNode = this.wrapper?.querySelector("[data-qa-stock-message]");
        if (!messageNode || !window.QuickAddInventory?.buildStockMessage) return;

        if (!this.inventoryApiData || !this.selectedVariant || !this.productData) {
          messageNode.hidden = true;
          messageNode.innerHTML = "";
          messageNode.classList.remove("is-highlighted");
          return;
        }

        const config =
          this.inventoryConfig || window.QuickAddInventory.loadConfig();
        const { html, highlighted } = window.QuickAddInventory.buildStockMessage(
          {
            inventoryData: this.inventoryApiData,
            variant: this.selectedVariant,
            product: this.productData,
            colorKey: this.getSelectedColorKey(),
            sizeKey: this.getSelectedSizeKey(),
            config,
          },
        );

        if (!html) {
          messageNode.hidden = true;
          messageNode.innerHTML = "";
          messageNode.classList.remove("is-highlighted");
          return;
        }

        messageNode.hidden = false;
        messageNode.innerHTML = html;
        messageNode.classList.toggle("is-highlighted", highlighted);
      }

      getSelectedSizeValue() {
        const sizeIndex = this.getSizeOptionIndex();
        if (sizeIndex < 0) return "";

        const checked = this.wrapper?.querySelector(
          `[data-quick-add-option][data-option-index="${sizeIndex}"]:checked`,
        );
        if (checked) return checked.value;

        const select = this.wrapper?.querySelector(
          `select[data-quick-add-option][data-option-index="${sizeIndex}"]`,
        );

        return select?.value || this.selectedVariant?.[`option${sizeIndex + 1}`] || "";
      }

      getCustomSizePanel() {
        return window.QuickAddCustomSize?.getPanelRoot?.(this.wrapper) || null;
      }

      getCustomSizeScope() {
        return this.wrapper;
      }

      isCustomSizeSelected() {
        if (!window.QuickAddCustomSize?.isCustomSizeValue) return false;
        return window.QuickAddCustomSize.isCustomSizeValue(
          this.getSelectedSizeValue(),
        );
      }

      updateCustomSizeVisibility() {
        const scope = this.getCustomSizeScope();
        if (!scope || !window.QuickAddCustomSize?.toggleVisibility) return;

        window.QuickAddCustomSize.toggleVisibility(
          scope,
          this.isCustomSizeSelected(),
        );
      }

      onCustomSizeInteraction(event) {
        const scope = this.getCustomSizeScope();
        if (!scope) return false;

        return Boolean(
          window.QuickAddCustomSize?.handlePanelInteraction?.(event, scope),
        );
      }

      normalizeProductTags(tags) {
        if (Array.isArray(tags)) return tags;
        if (typeof tags === "string") {
          return tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);
        }
        return [];
      }

      productHasFinalSaleTag() {
        return this.normalizeProductTags(this.productData?.tags).includes(
          "Final Sale",
        );
      }

      appendLineItemProperties(formData, properties = {}) {
        Object.entries(properties).forEach(([key, value]) => {
          if (value == null || value === "") return;
          formData.append(`properties[${key}]`, value);
        });
      }

      async initInventoryLightning() {
        if (!this.productData?.id) return;

        try {
          const response = await fetch(
            `https://api.kissprom.com/node/api/sitemap?name=product&id=${this.productData.id}&store=4`,
          );
          if (!response.ok) return;

          const payload = await response.json();
          this.inventoryApiData = payload?.data || null;
        } catch (error) {
          console.error("Quick add inventory fetch failed:", error);
          this.inventoryApiData = null;
        }

        this.inventoryConfig = window.QuickAddInventory?.loadConfig?.() || null;
        this.applyLightningIcons();
        this.updateStockMessage();
      }

      clearLightningIcons() {
        this.wrapper
          ?.querySelectorAll(".qa-lightning-overlay, .qa-lightning-badge")
          .forEach((node) => node.remove());
      }

      applyLightningIcons() {
        if (!this.wrapper) return;

        this.clearLightningIcons();
        if (!this.inventoryApiData) return;

        const inventory = this.inventoryApiData;

        Object.keys(inventory).forEach((colorKey) => {
          const sizeMap = inventory[colorKey];
          if (!sizeMap || !Object.values(sizeMap).includes(1)) return;

          this.wrapper
            .querySelectorAll(
              `[data-inventory-color-key="${CSS.escape(colorKey)}"]`,
            )
            .forEach((label) => {
              if (label.querySelector(".qa-lightning-overlay")) return;

              const overlay = document.createElement("div");
              overlay.className = "qa-lightning-overlay";
              overlay.innerHTML = QuickAddAjax.LIGHTNING_OVERLAY_SVG;
              label.appendChild(overlay);
            });
        });

        const selectedColorKey = this.getSelectedColorKey();
        if (!selectedColorKey) return;

        const sizeMap = inventory[selectedColorKey];
        if (!sizeMap) return;

        Object.entries(sizeMap).forEach(([sizeKey, stockFlag]) => {
          if (stockFlag !== 1) return;

          this.wrapper
            .querySelectorAll(
              `[data-inventory-size-key="${CSS.escape(sizeKey)}"]`,
            )
            .forEach((label) => {
              if (label.querySelector(".qa-lightning-badge")) return;

              const badge = document.createElement("span");
              badge.className = "qa-lightning-badge qa-lightning-badge--size";
              badge.innerHTML = QuickAddAjax.LIGHTNING_SVG;
              label.appendChild(badge);
            });
        });
      }

      formatMoney(cents) {
        const currency =
          window.Shopify?.currency?.active ||
          window.Shopify?.currency?.shopCurrency ||
          "USD";
        const locale = document.documentElement.lang || "en";

        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
        }).format((cents || 0) / 100);
      }

      updateOptionHeadings() {
        if (!this.wrapper || !this.selectedVariant || !this.productData) return;

        this.productData.options.forEach((option, index) => {
          const group = this.wrapper.querySelector(
            `.quick-add-ajax__option-group[data-option-index="${index}"]`,
          );
          if (!group) return;

          const selectedValue = this.selectedVariant[`option${index + 1}`] || "";
          const title = group.querySelector(".quick-add-ajax__option-title");
          if (!title) return;

          const nameEl = title.querySelector(".quick-add-ajax__option-name");
          const baseName =
            nameEl?.dataset.optionName ||
            nameEl?.textContent.replace(/:$/, "").trim() ||
            "";
          let valueEl = title.querySelector(".quick-add-ajax__option-value");
          title.querySelector(".quick-add-ajax__option-sep")?.remove();

          if (selectedValue) {
            if (nameEl) {
              nameEl.textContent = `${baseName}:`;
            }

            if (!valueEl) {
              valueEl = document.createElement("span");
              valueEl.className = "quick-add-ajax__option-value";
              title.appendChild(valueEl);
            }

            valueEl.textContent = selectedValue;
          } else {
            if (nameEl) {
              nameEl.textContent = baseName;
            }
            valueEl?.remove();
          }
        });
      }

      onDrawerChange(event) {
        if (!event.target.matches("[data-quick-add-option]")) return;
        if (event.target.type === "radio") return;
        this.handleOptionChange(event.target);
      }

      handleOptionChange(input) {
        if (!input?.matches("[data-quick-add-option]")) return;

        this.clearError();

        const optionValues = this.getSelectedOptions();
        const changedIndex = Number(input.dataset.optionIndex);

        this.selectedVariant =
          this.findVariantByOptions(optionValues) ||
          this.findClosestVariant(changedIndex, optionValues);
        this.updateProductView();
        this.updateCustomSizeVisibility();
      }

      async onDrawerClick(event) {
        if (this.onCustomSizeInteraction(event)) return;
        if (window.QuickAddSizeGuide?.handleInteraction?.(event)) return;

        const navLink = event.target.closest(
          ".quick-add-ajax__title-link, .quick-add-ajax__details-link",
        );

        if (navLink) {
          this.closeCart();
          return;
        }

        if (event.target.closest("[data-qa-gallery-prev]")) {
          event.preventDefault();
          this.navigateGallery(-1);
          return;
        }

        if (event.target.closest("[data-qa-gallery-next]")) {
          event.preventDefault();
          this.navigateGallery(1);
          return;
        }

        const optionLabel = event.target.closest(
          "label.quick-add-ajax__pill, label.f-swatch__list__link",
        );

        if (optionLabel) {
          const inputId = optionLabel.getAttribute("for");
          const input = inputId
            ? this.wrapper.querySelector(`#${CSS.escape(inputId)}`)
            : null;

          if (!input || input.disabled) return;

          if (input.type === "radio") {
            input.checked = true;
            input
              .closest(".quick-add-ajax__option-group")
              ?.querySelectorAll(".quick-add-ajax__pill.is-selected")
              .forEach((pill) => pill.classList.remove("is-selected"));
            optionLabel.classList.add("is-selected");
            this.handleOptionChange(input);
          }

          return;
        }

        const submitButton = event.target.closest("[data-quick-add-submit]");
        if (!submitButton) return;

        event.preventDefault();
        if (
          submitButton.disabled ||
          submitButton.classList.contains("is-pending")
        ) {
          return;
        }

        await this.addToCart(submitButton.dataset.variantId, {
          button: submitButton,
        });
      }

      onKeyDown(event) {
        if (event.key === "Escape") {
          if (window.QuickAddSizeGuide?.isOpen?.()) {
            window.QuickAddSizeGuide.close();
            event.preventDefault();
            return;
          }

          const panel = window.QuickAddCustomSize?.getPanelRoot?.(this.wrapper);
          const zoomOpen = panel
            ?.querySelector("[data-qa-custom-zoom-modal]")
            ?.classList.contains("is-open");

          if (zoomOpen) {
            window.QuickAddCustomSize?.closeZoom?.(panel);
            event.preventDefault();
            return;
          }

          this.closeCart();
        }
      }

      async addToCart(variantId, { button }) {
        if (!variantId || !button) return;

        const cart = document.querySelector("cart-drawer");
        const drawerWasOpen = this.isQuickBuyOpen();
        const customSizeScope = this.getCustomSizeScope();
        let customSizeProperties = {};

        this.clearError();

        if (this.isCustomSizeSelected() && customSizeScope) {
          const validation = window.QuickAddCustomSize?.validate?.(
            customSizeScope,
          );

          if (validation && !validation.valid) {
            this.showError(
              validation.message ||
                "Please fill in all required custom size measurements.",
            );
            return;
          }

          customSizeProperties = validation?.properties || {};
        }

        this.setButtonPending(button, true);

        const config =
          typeof fetchConfig === "function"
            ? fetchConfig("javascript")
            : {
                method: "POST",
                headers: {
                  Accept: "application/javascript",
                },
              };

        config.headers = config.headers || {};
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", "1");

        if (this.isCustomSizeSelected() && customSizeScope) {
          this.appendLineItemProperties(formData, customSizeProperties);
        }

        if (this.productHasFinalSaleTag()) {
          formData.append(
            "properties[Return Policy]",
            "The price is final sale, non-returnable",
          );
        }

        if (cart && typeof cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            cart.getSectionsToRender().map((section) => section.id),
          );
        } else {
          formData.append("sections", []);
        }

        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        try {
          const response = await fetch(`${window.routes.cart_add_url}`, config);
          const parsedResponse = await response.json();

          if (parsedResponse.status) {
            this.showError(parsedResponse.description || parsedResponse.message);
            return;
          }

          if (drawerWasOpen) {
            this.closeCart();
          }

          if (cart && typeof cart.renderContents === "function") {
            const isMobileSheet = window.matchMedia("(max-width: 899px)").matches;
            const shouldOpenCartDrawer = !isMobileSheet || !drawerWasOpen;
            const isClosedCart =
              shouldOpenCartDrawer &&
              !document.body.classList.contains("page-overlay-cart-on");

            cart.renderContents(parsedResponse, isClosedCart);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          this.showError(error.message || "Unable to add this item to cart.");
        } finally {
          this.setButtonPending(button, false);
          this.releaseDesktopCardButtonFocus();
        }
      }

      setButtonPending(button, isPending) {
        button.classList.toggle("is-pending", isPending);
        button.toggleAttribute("aria-busy", isPending);
      }

      showError(message) {
        const wrapper = this.wrapper?.querySelector(".quick-add-ajax__error-message");
        const messageNode = wrapper?.querySelector(".product-form__error-message");

        if (!wrapper || !messageNode) return;

        wrapper.hidden = false;
        messageNode.textContent = message;
      }

      clearError() {
        const wrapper = this.wrapper?.querySelector(".quick-add-ajax__error-message");
        const messageNode = wrapper?.querySelector(".product-form__error-message");

        if (!wrapper || !messageNode) return;

        wrapper.hidden = true;
        messageNode.textContent = "";
      }

      renderError(message) {
        if (!this.wrapper) return;

        this.wrapper
          .querySelector(".quick-add-ajax__panel--skeleton")
          ?.remove();

        const errorNode = document.createElement("div");
        errorNode.className = "quick-add-ajax__error";
        errorNode.innerHTML = `
          <p>${this.escapeHtml(message)}</p>
        `;

        this.wrapper.prepend(errorNode);
      }

      escapeHtml(value) {
        return String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      }
    },
  );
}
